import { FabButton, IRestaurant, IUpdateRestaurant, List, SearchField, StringUtils } from '@localina/core';
import { AddIcon } from '@localina/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAccount } from '../../api/queries/account';
import { useGetAuthUser } from '../../api/queries/auth';
import {
    useCurrentRestaurantId,
    useGetRestaurants,
    useUpdateSpecificRestaurantMutation,
} from '../../api/queries/restaurants';
import { Page, RestaurantListItem, RestaurantView } from '../../components';
import { RestaurantWizard } from '../../components/RestaurantWizard';

function prepareRestaurantObject(restaurant: IRestaurant): Partial<IUpdateRestaurant> {
    return {
        info: {
            ...restaurant.info,
            phoneNumber: StringUtils.removeSpace(restaurant.info.phoneNumber),
        },
        receiveReservationsEmails: Boolean(restaurant.configuration.receiveReservationsEmails),
        widgetWhiteLabeled: restaurant.configuration.widgetWhiteLabeled,
        platformIds: restaurant.configuration.platforms,
        cancelReservationNotice: restaurant.configuration.cancelReservationNotice,
    };
}

const Restaurants: React.FC = () => {
    const { t } = useTranslation();

    const [restaurant, setRestaurant] = React.useState<IRestaurant | undefined>(undefined);
    const [isWizardVisible, setWizardVisible] = React.useState(false);

    const [search, setSearch] = React.useState('');

    const authUserQuery = useGetAuthUser();
    const restaurantsQuery = useGetRestaurants();
    const accountQuery = useGetAccount();

    const currentRestaurantId = useCurrentRestaurantId();
    const updateRestaurantMutation = useUpdateSpecificRestaurantMutation();

    const handleItemClick = (item: IRestaurant) => {
        setRestaurant(item);
    };

    const handleClose = () => {
        setRestaurant(undefined);
    };

    const handleSave = (value: IRestaurant) => {
        updateRestaurantMutation.mutate(
            {
                restaurantId: value.id,
                restaurant: prepareRestaurantObject(value),
            },
            {
                onSuccess: () => {
                    setRestaurant(undefined);
                },
            },
        );
    };

    const handleWizardShow = () => {
        setWizardVisible(true);
    };

    const handleWizardClose = () => {
        setWizardVisible(false);
        void restaurantsQuery.refetch();
    };

    const isSupportUser = authUserQuery.isSuccess && authUserQuery.data.hasSupportRole;

    const restaurants = restaurantsQuery.data?.restaurants ?? [];
    const needle = search.trim().toLowerCase();
    const visibleRestaurants = needle
        ? restaurants.filter((item) =>
              [item.info.name, item.info.street, item.info.city, item.info.postalCode]
                  .filter(Boolean)
                  .some((field) => field.toLowerCase().includes(needle)),
          )
        : restaurants;

    const subscriptionOf = (restaurantId: string) =>
        accountQuery.data?.restaurants.find((entry) => entry.id === restaurantId)?.subscription;

    const subtitle = restaurantsQuery.isSuccess
        ? needle
            ? t('restaurants.subtitleFiltered', { shown: visibleRestaurants.length, total: restaurants.length })
            : t('restaurants.venuesCount', { count: restaurants.length })
        : undefined;

    return (
        <Page
            name="restaurants"
            title={{ value: t('restaurants.title'), subtitle }}
            isLoading={restaurantsQuery.isLoading || updateRestaurantMutation.isLoading}
        >
            <div className="restaurants__filter">
                <SearchField label={t('restaurants.search')} value={search} onChange={setSearch} onSearch={setSearch} />
            </div>
            <div className="restaurants__list">
                <List>
                    {visibleRestaurants.map((item) => (
                        <RestaurantListItem
                            key={item.id}
                            restaurant={item}
                            subscription={subscriptionOf(item.id)}
                            isCurrent={Boolean(currentRestaurantId) && item.id === currentRestaurantId}
                            onClick={handleItemClick}
                        />
                    ))}
                </List>
                {restaurantsQuery.isSuccess && visibleRestaurants.length === 0 && (
                    <div className="restaurants__empty">{t('restaurants.empty')}</div>
                )}
            </div>
            <RestaurantView
                restaurant={restaurant}
                hasError={updateRestaurantMutation.isError}
                onClose={handleClose}
                onSave={handleSave}
            />
            {isSupportUser && <FabButton icon={<AddIcon />} onClick={handleWizardShow} fixed />}
            {isWizardVisible && <RestaurantWizard onClose={handleWizardClose} />}
        </Page>
    );
};

export default Restaurants;
